import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Plus, FolderOpen, Users, Calendar, FileText, Pencil, LayoutGrid, List,
  ChevronLeft, ChevronRight, MessageSquare, Archive, RotateCcw, Trash2,
  Share2, Lock, Search, X, ArrowDownUp,
} from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const ITEMS_PER_PAGE_GRID = 15;
const ITEMS_PER_PAGE_LIST = 14;
const ACCENT = '#3b82f6';

const normalizeSearch = (value) => (value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/\p{Diacritic}/gu, '');

/** Aligne la forme API /list/archives sur celle de GET /api/projects */
const normalizeArchivedProject = (project) => ({
  ...project,
  project_status: 'archived',
  conversation_count: project.conversation_count
    ?? project.conversations_count
    ?? project.sessions_count
    ?? 0,
  members_count: project.members_count ?? 1,
  files_count: project.files_count ?? 0,
});

const filterChipStyle = (active) => ({
  padding: '5px 11px',
  borderRadius: 20,
  fontSize: 12,
  cursor: 'pointer',
  border: `1px solid ${active ? ACCENT : 'var(--card-border)'}`,
  background: active ? 'rgba(59,130,246,0.12)' : 'var(--card-bg)',
  color: active ? ACCENT : 'var(--text-secondary)',
  fontWeight: active ? 600 : 500,
  whiteSpace: 'nowrap',
  transition: 'all .15s',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
});

// Palette stable pour avatars membres (dérivée de l'ID, pas de l'index)
const AVATAR_PALETTE = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444', '#14b8a6'];
const avatarColor = (seed) => {
  const str = String(seed || '');
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const roleLabel = (r) => ({ creator: 'Créateur', admin: 'Admin', contributor: 'Collaborateur', reader: 'Lecteur' }[r] || r || '—');

const getProjectIcon = (type) => {
  switch (type) {
    case 'development': return '🚀';
    case 'expertise': return '🧠';
    case 'commercial': return '💼';
    case 'team': return '👥';
    case 'research': return '🔬';
    case 'documentation': return '📚';
    case 'system': return '🔒';
    default: return '📁';
  }
};

const getProjectLabel = (type) => {
  switch (type) {
    case 'development': return 'Développement';
    case 'expertise': return 'Base Expertise';
    case 'commercial': return 'Commercial';
    case 'team': case 'équipe': return 'Équipe';
    case 'research': case 'recherche': return 'Recherche';
    case 'documentation': return 'Documentation';
    case 'system': return 'Système';
    default: return 'Autre';
  }
};

// Chip partagé avec popover lazy-fetch des membres
const ShareIndicator = ({ projectId, membersCount }) => {
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef(null);

  const openPopover = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setOpen(true);
    if (!members && !loading) {
      setLoading(true);
      fetch(`/api/projects/${projectId}/team`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('oracle_token')}` }
      })
        .then(r => r.json())
        .then(d => setMembers(d.members || []))
        .catch(() => setMembers([]))
        .finally(() => setLoading(false));
    }
  };
  const schedClose = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <div
      onMouseEnter={openPopover}
      onMouseLeave={schedClose}
      onClick={e => e.stopPropagation()}
      style={{ position: 'relative' }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 10px', borderRadius: 999,
        fontSize: 11, fontWeight: 500,
        background: 'rgba(139,92,246,0.1)',
        color: '#c4b5fd',
        border: '1px solid rgba(139,92,246,0.22)',
        cursor: 'default',
        transition: 'all 0.18s',
        whiteSpace: 'nowrap'
      }}>
        <Share2 size={10} strokeWidth={2.3} />
        Partagé · {membersCount}
      </span>
      {open && (
        <div
          onMouseEnter={openPopover}
          onMouseLeave={schedClose}
          style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
            background: 'var(--modal-bg, #0f1628)',
            border: '1px solid var(--card-border)',
            borderRadius: 10, padding: '10px 12px', minWidth: 230, maxWidth: 280,
            boxShadow: '0 14px 34px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.08)',
            zIndex: 50,
            animation: 'fadeIn 0.14s ease-out'
          }}>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8, fontWeight: 600 }}>
            Membres du projet
          </div>
          {loading && <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '4px 0' }}>Chargement…</div>}
          {members && members.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Aucun membre</div>}
          {members && members.map((m) => {
            const label = m.full_name || m.username || '—';
            const color = avatarColor(m.user_id || m.username);
            const initials = (label[0] || '?').toUpperCase();
            return (
              <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', fontSize: 12 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`,
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 600, flexShrink: 0,
                  boxShadow: `0 2px 6px ${color}40`
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{label}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 10 }}>{roleLabel(m.role)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProjectsList = ({ onProjectSelect, onCreateProject, onLoadProjects, projects }) => {
  const [loading] = useState(false);
  const [error] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState(
    () => localStorage.getItem('itriia.projects.sortBy') || 'recent'
  );
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem('itriia.projects.viewMode') || 'grid'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [editingProject, setEditingProject] = useState(null);
  const [editName, setEditName] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editFormName, setEditFormName] = useState('');
  const [editFormDesc, setEditFormDesc] = useState('');
  const [editFormType, setEditFormType] = useState('development');
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);

  const projectTypeOptions = [
    { value: 'development', label: 'Développement' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'team', label: 'Équipe' },
    { value: 'research', label: 'Recherche' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'comm', label: 'Communication' },
    { value: 'expertise', label: 'Expertise' },
    { value: 'personal', label: 'Personnel' },
    { value: 'system', label: 'Système' },
  ];

  // Identifier l'utilisateur courant pour distinguer projets invités
  const currentUserId = useMemo(() => {
    try {
      const token = localStorage.getItem('oracle_token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id;
    } catch { return null; }
  }, []);

  // Quota projets (collaborateurs plafonnés, admin/fondateur illimités)
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('oracle_token');
    if (!token) return;
    fetch('/api/projects/quota', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setQuota(data); })
      .catch(() => {});
  }, [projects]);

  const quotaBlocked = !!(quota && !quota.unlimited && quota.current >= quota.max);
  const quotaTooltip = quotaBlocked
    ? `Limite atteinte : ${quota.max} projets actifs. Archive un projet pour en créer un nouveau.`
    : (quota && !quota.unlimited ? `${quota.current} / ${quota.max} projets actifs` : null);

  useEffect(() => {
    localStorage.setItem('itriia.projects.viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('itriia.projects.sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeFilter, typeFilter, sortBy, viewMode]);

  const loadArchivedProjects = useCallback(async () => {
    const token = localStorage.getItem('oracle_token');
    if (!token) {
      setArchivedProjects([]);
      return;
    }
    setArchivesLoading(true);
    try {
      const res = await fetch('/api/projects/list/archives', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const rows = await res.json();
        setArchivedProjects((rows || []).map(normalizeArchivedProject));
      }
    } catch (e) {
      console.error('Erreur chargement archives projets:', e);
      setArchivedProjects([]);
    } finally {
      setArchivesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchivedProjects();
  }, [loadArchivedProjects, projects]);

  const isGuestProject = (project) => (
    currentUserId && project.creator_id && project.creator_id !== currentUserId
  );

  const filteredProjects = useMemo(() => {
    const q = normalizeSearch(search.trim());
    const baseList = activeFilter === 'archived' ? archivedProjects : (projects || []);

    let list = baseList.filter((project) => {
      if (activeFilter === 'mine') {
        if (!currentUserId || project.creator_id !== currentUserId) return false;
      } else if (activeFilter === 'shared') {
        if (!isGuestProject(project)) return false;
      } else if (activeFilter === 'active') {
        if (project.project_status === 'archived') return false;
      }

      if (typeFilter && project.project_type !== typeFilter) return false;

      if (!q) return true;
      const haystack = normalizeSearch([
        project.name,
        project.description,
        getProjectLabel(project.project_type),
      ].join(' '));
      return haystack.includes(q);
    });

    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '', 'fr');
        case 'conversations':
          return (b.conversation_count || 0) - (a.conversation_count || 0);
        case 'files':
          return (b.files_count || 0) - (a.files_count || 0);
        case 'recent':
        default:
          return new Date(b.archived_at || b.created_at || 0)
            - new Date(a.archived_at || a.created_at || 0);
      }
    });

    return list;
  }, [projects, archivedProjects, search, activeFilter, typeFilter, sortBy, currentUserId]);

  const typeCounts = useMemo(() => {
    const counts = {};
    (projects || []).forEach((p) => {
      const t = p.project_type || 'other';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [projects]);

  const handleCreateClick = () => {
    if (quotaBlocked) {
      toast.warning(`Limite atteinte : ${quota.max} projets actifs maximum. Archive un projet pour en créer un nouveau.`);
      return;
    }
    onCreateProject();
  };

  const itemsPerPage = viewMode === 'grid' ? ITEMS_PER_PAGE_GRID : ITEMS_PER_PAGE_LIST;
  const totalPages = Math.max(1, Math.ceil((filteredProjects.length || 0) / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProjects = filteredProjects.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  const renameProject = async (projectId) => {
    if (!editName.trim()) { setEditingProject(null); return; }
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() })
      });
      if (res.ok) onLoadProjects();
      else { const d = await res.json().catch(() => ({})); toast.error(d.message || d.error || 'Erreur renommage'); }
    } catch (e) { console.error('Erreur rename:', e); }
    setEditingProject(null);
  };

  const saveProjectEdit = async () => {
    if (!editModal || !editFormName.trim()) return;
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/projects/${editModal.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editFormName.trim(),
          description: editFormDesc.trim(),
          project_type: editFormType,
        })
      });
      if (res.ok) onLoadProjects();
      else { const d = await res.json().catch(() => ({})); toast.error(d.message || d.error || 'Erreur modification'); }
    } catch (e) { console.error('Erreur edit:', e); }
    setEditModal(null);
  };

  const deleteProject = async (projectId, projectName, e) => {
    e.stopPropagation();
    if (!await confirmModal(`Supprimer "${projectName}" ?`)) return;
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        onLoadProjects();
        toast.success('Projet supprimé');
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const reactivateProject = async (projectId, projectName, e) => {
    e.stopPropagation();
    if (!await confirmModal(`Réactiver "${projectName}" ?`)) return;
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) onLoadProjects();
      else toast.error('Erreur lors de la réactivation');
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    } finally {
      loadArchivedProjects();
    }
  };

  const archiveProject = async (projectId, projectName, e) => {
    e.stopPropagation();
    if (!await confirmModal(`Archiver "${projectName}" ?`)) return;
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/archive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'manual_interface' })
      });
      if (response.ok) {
        onLoadProjects();
        toast.success('Projet archivé');
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.message || data.error || "Erreur lors de l'archivage");
      }
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTypeBadgeStyle = (type) => {
    const colors = {
      development:   '#818cf8',
      expertise:     '#c084fc',
      commercial:    '#34d399',
      team:          '#fbbf24',
      équipe:        '#2dd4bf',
      research:      '#60a5fa',
      recherche:     '#fb923c',
      documentation: '#60a5fa',
      system:        '#f87171',
    };
    const color = colors[type] || '#9ca3af';
    return {
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: 'transparent',
      color,
      border: `1px solid ${color}33`,
      padding: '3px 10px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.2px',
      whiteSpace: 'nowrap',
      opacity: 0.92
    };
  };

  // Badge discret unifié pour Invité / Confidentiel / Archivé
  const neutralBadgeStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 6,
    fontSize: 10, fontWeight: 500, letterSpacing: '0.2px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--card-border)',
    whiteSpace: 'nowrap'
  };

  const ActionButtons = ({ project, isArchived }) => (
    <div style={{ display: 'flex', gap: '4px', opacity: 0, transition: 'opacity 0.2s' }} className="action-btns">
      {isArchived ? (
        <button onClick={(e) => reactivateProject(project.id, project.name, e)} title="Réactiver"
          style={{ ...actionBtnStyle, background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
          <RotateCcw size={12} />
        </button>
      ) : (
        <>
          <button onClick={(e) => { e.stopPropagation(); setEditFormName(project.name); setEditFormDesc(project.description || ''); setEditFormType(project.project_type || 'development'); setEditModal(project); }} title="Éditer"
            style={{ ...actionBtnStyle, background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>
            <Pencil size={12} />
          </button>
          {project.conversation_count >= 1 && (
            <button onClick={(e) => archiveProject(project.id, project.name, e)} title="Archiver"
              style={{ ...actionBtnStyle, background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>
              <Archive size={12} />
            </button>
          )}
          <button onClick={(e) => deleteProject(project.id, project.name, e)} title="Supprimer"
            style={{ ...actionBtnStyle, background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
            <Trash2 size={12} />
          </button>
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px', gap: '12px' }}>
        <div style={{ width: 32, height: 32, border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ color: 'var(--text-secondary)' }}>Chargement des projets...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ color: '#ef4444', fontSize: '18px', marginBottom: '16px' }}>Erreur de chargement</div>
        <div style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>{error}</div>
        <button onClick={() => window.location.reload()}
          style={{ background: '#1D4ED8', color: '#fff', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .project-card:hover .action-btns { opacity: 1 !important; }
        .project-row:hover .action-btns { opacity: 1 !important; }
        .project-card:hover { transform: translateY(-2px); box-shadow: 0 6px 22px rgba(29,78,216,0.1) !important; border-color: rgba(59,130,246,0.28) !important; }
        .project-row:hover { background: var(--sidebar-active-bg) !important; }
        .view-btn:hover { background: rgba(59,130,246,0.15) !important; }
        .page-btn:hover:not(:disabled) { background: rgba(59,130,246,0.15) !important; color: #3b82f6 !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '26px', fontWeight: 700, color: 'var(--text)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FolderOpen size={24} style={{ color: '#3b82f6' }} />
            Projets Collaboratifs
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
            {filteredProjects.length === projects.length
              ? `${projects.length} projet${projects.length > 1 ? 's' : ''} · mémoire continue`
              : `${filteredProjects.length} sur ${projects.length} projet${projects.length > 1 ? 's' : ''}`}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleCreateClick}
            disabled={quotaBlocked}
            title={quotaTooltip || undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: quotaBlocked
                ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
                : 'linear-gradient(135deg, #1D4ED8 0%, #2563eb 100%)',
              color: '#fff', fontWeight: 600, padding: '10px 20px', borderRadius: '10px',
              border: 'none', cursor: quotaBlocked ? 'not-allowed' : 'pointer', fontSize: '14px',
              boxShadow: quotaBlocked ? 'none' : '0 4px 14px rgba(29,78,216,0.35)',
              opacity: quotaBlocked ? 0.65 : 1,
              transition: 'all 0.2s'
            }}>
            <Plus size={18} />
            {quotaBlocked ? `Limite atteinte (${quota.max})` : 'Nouveau Projet'}
          </button>
        </div>
      </div>

      {/* Barre outils — recherche · filtres · tri · vue */}
      {projects.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          marginBottom: 18, flexShrink: 0,
        }}>
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180, maxWidth: 320 }}>
            <Search size={14} style={{
              position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%',
              color: 'var(--text-secondary)', pointerEvents: 'none',
            }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un projet…"
              style={{
                width: '100%', padding: '8px 30px 8px 32px', borderRadius: 10, fontSize: 13,
                background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)',
                border: '1px solid var(--card-border)', outline: 'none', boxSizing: 'border-box',
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                title="Effacer"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary)', padding: 2, display: 'flex',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button type="button" style={filterChipStyle(activeFilter === 'all')} onClick={() => setActiveFilter('all')}>Tous</button>
            <button type="button" style={filterChipStyle(activeFilter === 'active')} onClick={() => setActiveFilter('active')}>Actifs</button>
            <button type="button" style={filterChipStyle(activeFilter === 'mine')} onClick={() => setActiveFilter('mine')}>Mes projets</button>
            <button type="button" style={filterChipStyle(activeFilter === 'shared')} onClick={() => setActiveFilter('shared')}>
              <Share2 size={11} /> Partagés
            </button>
            <button type="button" style={filterChipStyle(activeFilter === 'archived')} onClick={() => setActiveFilter('archived')}>
              <Archive size={11} /> Archivés{archivedProjects.length ? ` (${archivedProjects.length})` : ''}
            </button>
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              padding: '7px 10px', borderRadius: 10, fontSize: 12,
              background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)',
              border: '1px solid var(--card-border)', cursor: 'pointer',
            }}
          >
            <option value="">Tous types</option>
            {projectTypeOptions.filter((t) => typeCounts[t.value]).map((t) => (
              <option key={t.value} value={t.value}>{t.label} ({typeCounts[t.value]})</option>
            ))}
          </select>

          <div style={{ flex: 1, minWidth: 8 }} />

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowDownUp size={13} style={{ color: 'var(--text-secondary)' }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '7px 10px', borderRadius: 10, fontSize: 12,
                background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)',
                border: '1px solid var(--card-border)', cursor: 'pointer',
              }}
            >
              <option value="recent">Plus récents</option>
              <option value="name">Nom A→Z</option>
              <option value="conversations">Conversations</option>
              <option value="files">Fichiers</option>
            </select>
          </div>

          <div style={{
            display: 'inline-flex', border: '1px solid var(--card-border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <button type="button" className="view-btn" onClick={() => setViewMode('grid')} title="Vue grille"
              style={{
                padding: '7px 10px', border: 'none', cursor: 'pointer', display: 'flex',
                background: viewMode === 'grid' ? 'rgba(59,130,246,0.12)' : 'var(--card-bg)',
                color: viewMode === 'grid' ? ACCENT : 'var(--text-secondary)',
              }}>
              <LayoutGrid size={15} />
            </button>
            <button type="button" className="view-btn" onClick={() => setViewMode('list')} title="Vue liste"
              style={{
                padding: '7px 10px', border: 'none', cursor: 'pointer', display: 'flex',
                borderLeft: '1px solid var(--card-border)',
                background: viewMode === 'list' ? 'rgba(59,130,246,0.12)' : 'var(--card-bg)',
                color: viewMode === 'list' ? ACCENT : 'var(--text-secondary)',
              }}>
              <List size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Contenu */}
      {projects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 0', flex: 1 }}>
          <FolderOpen size={56} style={{ color: 'var(--text-secondary)', opacity: 0.4, marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>Aucun projet</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>Créez votre premier projet collaboratif ITRIIA</p>
          <button
            onClick={handleCreateClick}
            disabled={quotaBlocked}
            title={quotaTooltip || undefined}
            style={{
              background: quotaBlocked ? '#64748b' : '#1D4ED8',
              color: '#fff', padding: '10px 24px', borderRadius: '10px', border: 'none',
              cursor: quotaBlocked ? 'not-allowed' : 'pointer', fontWeight: 500,
              opacity: quotaBlocked ? 0.65 : 1,
            }}>
            {quotaBlocked ? `Limite atteinte (${quota.max})` : 'Créer un projet'}
          </button>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', flex: 1 }}>
          {archivesLoading && activeFilter === 'archived' ? (
            <>
              <div style={{
                width: 32, height: 32, margin: '0 auto 12px',
                border: '2px solid #3b82f6', borderTopColor: 'transparent',
                borderRadius: '50%', animation: 'spin 1s linear infinite',
              }} />
              <p style={{ color: 'var(--text-secondary)' }}>Chargement des archives…</p>
            </>
          ) : (
            <>
              <Search size={40} style={{ color: 'var(--text-secondary)', opacity: 0.35, marginBottom: 12 }} />
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                {activeFilter === 'archived' ? 'Aucun projet archivé' : 'Aucun projet ne correspond'}
              </h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>
                {activeFilter === 'archived'
                  ? 'Les projets archivés apparaissent ici et dans Projets → Archives.'
                  : 'Essayez un autre filtre ou effacez la recherche.'}
              </p>
              {activeFilter === 'archived' ? (
                <button
                  type="button"
                  onClick={() => { window.location.hash = 'archives'; }}
                  style={{
                    background: 'transparent', color: ACCENT, padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${ACCENT}44`, cursor: 'pointer', fontSize: 13, marginRight: 8,
                  }}
                >
                  Ouvrir la page Archives
                </button>
              ) : null}
              {activeFilter !== 'archived' && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setActiveFilter('all'); setTypeFilter(''); }}
                  style={{
                    background: 'transparent', color: ACCENT, padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${ACCENT}44`, cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>

          {/* === VUE GRILLE === */}
          {viewMode === 'grid' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(272px, 1fr))',
              gap: '12px',
            }}>
              {paginatedProjects.map((project) => {
                const isArchived = project.project_status === 'archived';
                const isGuest = isGuestProject(project);
                const hasDescription = !!(project.description && project.description.trim());
                return (
                  <div key={project.id} className="project-card"
                    onClick={() => !isArchived && onProjectSelect(project.id)}
                    style={{
                      background: isGuest
                        ? 'linear-gradient(135deg, rgba(139,92,246,0.06) 0%, var(--card-bg) 100%)'
                        : 'var(--card-bg)',
                      borderRadius: '12px', padding: '14px 15px',
                      border: isGuest ? '1px solid rgba(139,92,246,0.2)' : '1px solid var(--card-border)',
                      cursor: isArchived ? 'default' : 'pointer',
                      transition: 'all 0.2s ease', position: 'relative',
                      boxShadow: 'var(--shadow)',
                      opacity: isArchived ? 0.62 : 1,
                      display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0,
                    }}>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
                      <span style={{
                        fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: 1,
                      }}>
                        {getProjectIcon(project.project_type)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {editingProject === project.id ? (
                          <input autoFocus value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onBlur={() => renameProject(project.id)}
                            onKeyDown={e => { if (e.key === 'Enter') renameProject(project.id); if (e.key === 'Escape') setEditingProject(null); }}
                            onClick={e => e.stopPropagation()}
                            style={{
                              fontWeight: 600, width: '100%', background: 'transparent',
                              border: 'none', borderBottom: `2px solid ${ACCENT}`,
                              color: 'var(--text)', outline: 'none', fontSize: 14, padding: '1px 0',
                            }}
                          />
                        ) : (
                          <h3
                            style={{
                              fontWeight: 600, color: 'var(--text)', fontSize: 14, margin: 0,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                            onDoubleClick={e => { e.stopPropagation(); setEditName(project.name); setEditingProject(project.id); }}
                            title={project.name}
                          >
                            {project.name}
                          </h3>
                        )}
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6, alignItems: 'center',
                        }}>
                          <span style={{ ...getTypeBadgeStyle(project.project_type), padding: '2px 8px', fontSize: 10 }}>
                            {getProjectLabel(project.project_type)}
                          </span>
                          {isGuest && (
                            <>
                              <span style={{ ...neutralBadgeStyle, padding: '2px 7px', fontSize: 9 }}>
                                <Share2 size={8} /> Invité
                              </span>
                              <span style={{ ...neutralBadgeStyle, padding: '2px 7px', fontSize: 9 }}>
                                <Lock size={8} /> Confidentiel
                              </span>
                            </>
                          )}
                          {isArchived && (
                            <span style={{ ...neutralBadgeStyle, padding: '2px 7px', fontSize: 9 }}>Archivé</span>
                          )}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, opacity: 0, transition: 'opacity 0.2s' }} className="action-btns">
                        <ActionButtons project={project} isArchived={isArchived} />
                      </div>
                    </div>

                    {hasDescription && (
                      <p style={{
                        color: 'var(--text-secondary)', fontSize: 12, margin: 0, lineHeight: 1.45,
                        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {project.description}
                      </p>
                    )}

                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      fontSize: 11, color: 'var(--text-secondary)', marginTop: 'auto',
                      paddingTop: 8, borderTop: '1px solid var(--card-border)',
                    }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, minWidth: 0 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Users size={11} /> {project.members_count || 1}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <MessageSquare size={11} /> {project.conversation_count || 0}
                        </span>
                        {(project.files_count || 0) > 0 && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <FileText size={11} /> {project.files_count}
                          </span>
                        )}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={11} /> {formatDate(project.archived_at || project.created_at)}
                        </span>
                      </div>
                      {!isGuest && (project.members_count || 0) > 1 && (
                        <ShareIndicator projectId={project.id} membersCount={project.members_count} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* === VUE LISTE === */}
          {viewMode === 'list' && (
            <div style={{ background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)', overflow: 'hidden' }}>
              {/* Header tableau */}
              <div style={{
                display: 'grid', gridTemplateColumns: '2.5fr 1fr 0.8fr 0.8fr 0.8fr 100px',
                padding: '12px 20px', borderBottom: '1px solid var(--card-border)',
                fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px',
                background: 'rgba(59,130,246,0.04)'
              }}>
                <span>Projet</span>
                <span>Type</span>
                <span style={{ textAlign: 'center' }}>Conversations</span>
                <span style={{ textAlign: 'center' }}>Fichiers</span>
                <span>Créé le</span>
                <span></span>
              </div>

              {/* Lignes */}
              {paginatedProjects.map((project, idx) => {
                const isArchived = project.project_status === 'archived';
                const isGuest = isGuestProject(project);
                return (
                  <div key={project.id} className="project-row"
                    onClick={() => !isArchived && onProjectSelect(project.id)}
                    style={{
                      display: 'grid', gridTemplateColumns: '2.5fr 1fr 0.8fr 0.8fr 0.8fr 100px',
                      padding: '14px 20px', alignItems: 'center',
                      borderBottom: idx < paginatedProjects.length - 1 ? '1px solid var(--card-border)' : 'none',
                      cursor: isArchived ? 'default' : 'pointer',
                      transition: 'background 0.15s', opacity: isArchived ? 0.55 : 1,
                      background: isGuest ? 'var(--shared-tint)' : 'transparent',
                      borderLeft: isGuest ? '3px solid var(--shared-border)' : '3px solid transparent',
                    }}>
                    {/* Nom + description */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>{getProjectIcon(project.project_type)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={project.name}>
                          {project.name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
                          {project.description || '—'}
                        </div>
                      </div>
                    </div>

                    {/* Type badge */}
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={getTypeBadgeStyle(project.project_type)}>{getProjectLabel(project.project_type)}</span>
                      {isGuest && (
                        <>
                          <span style={neutralBadgeStyle}>
                            <Share2 size={9} /> Invité
                          </span>
                          <span
                            title="Projet partagé — ne pas diffuser le contenu à l'extérieur"
                            style={neutralBadgeStyle}>
                            <Lock size={9} /> Confidentiel
                          </span>
                        </>
                      )}
                      {isArchived && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#9ca3af' }}>Archivé</span>}
                    </div>

                    {/* Conversations */}
                    <div style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text)', fontWeight: 500 }}>
                      {project.conversation_count || 0}
                      {project.estimated_size_kb ? <span style={{ fontSize: '10px', color: 'var(--text-secondary)', marginLeft: '4px' }}>~{project.estimated_size_kb}KB</span> : null}
                    </div>

                    {/* Fichiers */}
                    <div style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text)', fontWeight: 500 }}>
                      {project.files_count || 0}
                    </div>

                    {/* Date */}
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {formatDate(project.created_at)}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <ActionButtons project={project} isArchived={isArchived} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          paddingTop: '20px', flexShrink: 0
        }}>
          <button className="page-btn" disabled={safePage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            style={{ ...pageBtnStyle, opacity: safePage <= 1 ? 0.35 : 1, cursor: safePage <= 1 ? 'default' : 'pointer' }}>
            <ChevronLeft size={16} />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button key={page} className="page-btn"
              onClick={() => setCurrentPage(page)}
              style={{
                ...pageBtnStyle,
                background: page === safePage ? '#1D4ED8' : 'transparent',
                color: page === safePage ? '#fff' : 'var(--text-secondary)',
                fontWeight: page === safePage ? 700 : 500,
                minWidth: '34px',
                boxShadow: page === safePage ? '0 2px 8px rgba(29,78,216,0.35)' : 'none'
              }}>
              {page}
            </button>
          ))}

          <button className="page-btn" disabled={safePage >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            style={{ ...pageBtnStyle, opacity: safePage >= totalPages ? 0.35 : 1, cursor: safePage >= totalPages ? 'default' : 'pointer' }}>
            <ChevronRight size={16} />
          </button>

          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '12px' }}>
            {(safePage - 1) * itemsPerPage + 1}–{Math.min(safePage * itemsPerPage, filteredProjects.length)} sur {filteredProjects.length}
          </span>
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '20px', flexShrink: 0,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, rgba(139,92,246,0.05) 100%)',
        border: '1px solid var(--card-border)', borderRadius: '12px',
        padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', boxShadow: '0 0 6px rgba(34,197,94,0.5)' }} />
            Le Relais ITRIIA
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            Mémoire persistante · Routage LLM intelligent · Base Expertise Métier modulaire
          </div>
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          {[
            { label: 'Projets', value: projects?.length || 0, color: '#1D4ED8' },
            { label: 'Modèles', value: '20', color: '#8b5cf6' },
            { label: 'Providers', value: '7', color: '#2563eb' }
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '9px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Édition */}
      {editModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }} onClick={() => setEditModal(null)}>
          <div style={{
            background: 'var(--modal-bg, var(--card-bg))', border: '1px solid var(--card-border)',
            borderRadius: '16px', width: '90vw', maxWidth: '480px', padding: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: 'var(--text)', fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
              Éditer le projet
            </h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Nom du projet</label>
              <input value={editFormName} onChange={e => setEditFormName(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Description</label>
              <textarea value={editFormDesc} onChange={e => setEditFormDesc(e.target.value)} rows={3}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Catégorie</label>
              <select value={editFormType} onChange={e => setEditFormType(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '14px', outline: 'none', cursor: 'pointer', boxSizing: 'border-box' }}>
                {projectTypeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditModal(null)} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px' }}>Annuler</button>
              <button onClick={saveProjectEdit} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: '#1D4ED8', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '13px', boxShadow: '0 2px 8px rgba(29,78,216,0.35)' }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const actionBtnStyle = {
  padding: '5px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px',
  transition: 'all 0.15s', fontWeight: 500
};

const pageBtnStyle = {
  padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--card-border)',
  background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
  fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all 0.15s'
};

export default ProjectsList;
